import type { ArtistLink } from '../types';

/** Совпадает с `ARTIST_LINKS_LIMIT` в `apps/api/src/artists/artist.dto.ts`. */
export const ARTIST_LINKS_LIMIT = 5;

/** Максимальная длина подписи из `artistLinkSchema`. */
export const ARTIST_LINK_LABEL_LIMIT = 32;

/** Подсказки для пустых строк: чаще всего добавляют именно их. */
export const LINK_LABEL_SUGGESTIONS = ['Website', 'Hipolink', 'ArtStation', 'Boosty'];

/** Строка формы: подпись и адрес редактируются отдельно, обе могут быть пустыми. */
export interface LinkDraft {
  label: string;
  url: string;
}

export function toDrafts(links: ArtistLink[]): LinkDraft[] {
  return links.length > 0 ? links.map((link) => ({ ...link })) : [{ label: '', url: '' }];
}

/**
 * Подпись по адресу: художник вставил ссылку на галерею и не стал её называть —
 * пусть в профиле будет домен, а не пустая кнопка.
 */
export function labelFromUrl(url: string): string {
  const host = hostOf(url);

  if (!host) {
    return 'Ссылка';
  }

  const name = host.replace(/^www\./, '').split('.')[0] ?? host;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/**
 * В API уходят только заполненные строки: пустая пара «подпись — адрес» это
 * не ссылка, а незаполненное поле формы.
 */
export function toArtistLinks(drafts: LinkDraft[]): ArtistLink[] {
  return drafts
    .map((draft) => ({ label: draft.label.trim(), url: draft.url.trim() }))
    .filter((draft) => draft.url.length > 0)
    .map((draft) => ({
      label: (draft.label || labelFromUrl(draft.url)).slice(0, ARTIST_LINK_LABEL_LIMIT),
      url: draft.url,
    }));
}

/** Проверка совпадает с серверной: в `href` попадают только http и https. */
export function isSupportedLinkUrl(url: string): boolean {
  return /^https?:\/\/\S+$/i.test(url.trim());
}

export function hasInvalidLink(drafts: LinkDraft[]): boolean {
  return drafts.some((draft) => draft.url.trim().length > 0 && !isSupportedLinkUrl(draft.url));
}

function hostOf(url: string): string | null {
  try {
    return new URL(url.trim()).hostname;
  } catch {
    return null;
  }
}
